import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Slide from '@mui/material/Slide'
import {
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
  useReactFlow,
  useNodesInitialized,
  useStoreApi,
  type NodeChange,
  type Viewport,
} from '@xyflow/react'
import {
  Alert,
  Box,
  CircularProgress,
  Snackbar,
  Typography,
} from '@mui/material'
import {
  CONNECTION_REFUSAL_TEXT,
  connectionRefusal,
} from '@/alerts/connectionRefusal'
import { useParams } from 'react-router-dom'
import '@xyflow/react/dist/style.css'
import { useAppTheme } from '@/theme/ThemeContext'

import DatePicker from '@assets/flowchart/DatePicker.svg'
import Info from '@assets/flowchart/Info.svg'
import Meter from '@assets/flowchart/Meter.svg'
import Network from '@assets/flowchart/Network.svg'
import Message from '@assets/flowchart/Message.svg'
import End from '@assets/flowchart/End.svg'
import Chart from '@assets/flowchart/Chart.svg'

import Sidebar from './flow-chart/Sidebar'
import Header from './flow-chart/Header'
import ConnectNode from './flow-chart/nodes/ConnectNode'
import TimeNode from './flow-chart/nodes/TimeNode'
import ConsumptionNode from './flow-chart/nodes/ConsumptionNode'
import MeterNode from './flow-chart/nodes/MeterNode'
import NetworkNode from './flow-chart/nodes/NetworkNode'
import MessageNode from './flow-chart/nodes/MessageNode'
import CrmNode from './flow-chart/nodes/CrmNode'
import EndNode from './flow-chart/nodes/EndNode'

import { ReferenceProvider } from './flow-chart/ReferenceContext'
import { FlowStateProvider, useFlowState } from './flow-chart/FlowStateContext'
import {
  EDITOR_MIN_ZOOM,
  fitViewOptions,
  placeInPane,
  visiblePane,
} from './flow-chart/placement'
import {
  fromSchemaTree,
  toSchemaTree,
  UnsupportedTreeError,
} from '@/utils/treeSerializer'
import {
  getActiveTree,
  getReferenceBundle,
} from '@/services/notificationService'
import { channelIdsByKey, knownChannelIds } from './flow-chart/referenceOptions'

const ROOT_NOT_AFFILIATION_NOTICE = 'העץ צריך להתחיל ב"מאפיין שייכות"'

const blankRoot = {
  id: 'root',
  type: 'connection',
  position: { x: 250, y: 150 },
  data: { label: 'Connect Node', isDefault: false },
}

const NotificationFlowInner = () => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const { notificationId, siteId } = useParams()
  const [openSidebar, setOpenSidebar] = useState(true)

  // מאפיין תקשורת is internal-only (operator decision 2026-07-15): hidden
  // from the sidebar unless the internal-nodes flag is set in localStorage.
  const showInternalNodes = localStorage.getItem('internal-nodes') === '1'
  const items = useMemo(
    () => [
      { id: 1, title: 'מאפיין שייכות', icon: Info, type: 'connection' },
      { id: 2, title: 'מאפיין זמן', icon: DatePicker, type: 'time' },
      { id: 3, title: 'מאפיין צריכה', icon: Chart, type: 'consumption' },
      { id: 4, title: 'התראת מד', icon: Meter, type: 'meter' },
      ...(showInternalNodes
        ? [{ id: 5, title: 'מאפיין תקשורת', icon: Network, type: 'network' }]
        : []),
      { id: 6, title: 'מאפיין שליחה', icon: Message, type: 'message' },
      { id: 8, title: 'סיומת', icon: End, type: 'end' },
    ],
    [showInternalNodes]
  )

  const nodeTypes = useMemo(
    () => ({
      connection: ConnectNode,
      time: TimeNode,
      consumption: ConsumptionNode,
      meter: MeterNode,
      network: NetworkNode,
      message: MessageNode,
      crm: CrmNode,
      end: EndNode,
    }),
    []
  )

  const flowState = useFlowState()
  const flowStore = useStoreApi()
  const { setViewport, fitView } = useReactFlow()
  const nodesInitialized = useNodesInitialized()
  const headerRef = useRef<HTMLDivElement>(null)
  const [nodes, setNodes, onNodesChange] = useNodesState<any>(
    flowState.initialNodes as any
  )
  const [edges, setEdges, onEdgesChange] = useEdgesState<any>(
    flowState.initialEdges as any
  )
  // hydrating = we're loading an existing alert's tree from the API; show an overlay until done
  const [hydrating, setHydrating] = useState<boolean>(
    !!(notificationId && notificationId !== 'new')
  )

  // Set when the saved tree holds something the editor would change on save.
  // The editor stays closed so no save can overwrite the tree.
  const [refusal, setRefusal] = useState<string | null>(null)
  const [connectionNotice, setConnectionNotice] = useState<string | null>(null)

  // The tree on screen is fitted once it is measured: at first for the blank
  // root, and again for a loaded tree. The `fitView` prop would fit only the
  // blank root and would ignore the header.
  const fitPending = useRef(true)
  useEffect(() => {
    const header = headerRef.current
    const { nodes: shown } = flowStore.getState()
    const measured =
      shown.length > 0 &&
      shown.every(n => n.measured?.width && n.measured?.height)
    if (!fitPending.current || hydrating || !nodesInitialized) return
    if (!header || !measured) return
    fitPending.current = false
    fitView(
      fitViewOptions(visiblePane(flowStore.getState(), header).coveredTop)
    )
  }, [nodesInitialized, hydrating, fitView, flowStore])

  // A node can grow after it is first measured, such as the send node when its
  // variable chips load. Until the author moves the canvas or a node, the
  // view follows the tree so the whole tree stays in view.
  const following = useRef(true)
  const [sizeChanges, setSizeChanges] = useState(0)
  useEffect(() => {
    const header = headerRef.current
    if (
      sizeChanges === 0 ||
      !following.current ||
      !header ||
      fitPending.current
    )
      return
    const frame = requestAnimationFrame(() =>
      fitView(
        fitViewOptions(visiblePane(flowStore.getState(), header).coveredTop)
      )
    )
    return () => cancelAnimationFrame(frame)
  }, [sizeChanges, fitView, flowStore])

  // Sync local nodes/edges with the shared context so Header.tsx can read them on Save
  useEffect(() => {
    flowState.setNodes(nodes as any)
  }, [nodes])
  useEffect(() => {
    flowState.setEdges(edges as any)
  }, [edges])

  // Hydrate from API when editing an existing alert
  useEffect(() => {
    if (!siteId || !notificationId || notificationId === 'new') {
      setHydrating(false)
      return
    }
    setHydrating(true)
    setRefusal(null)
    flowState.setLoaded(null)
    flowState.setTreeCalendar(undefined)
    flowState.setTreeCalendarIssues([])
    // The channel list maps keys and checks ids. When it cannot be read, a tree
    // with keys is refused and a tree with ids opens.
    Promise.all([
      getActiveTree(siteId, Number(notificationId)),
      getReferenceBundle(siteId).then(
        bundle => ({
          keys: channelIdsByKey(bundle),
          ids: knownChannelIds(bundle),
        }),
        () => ({
          keys: new Map<string, number>() as ReadonlyMap<string, number>,
          ids: undefined,
        })
      ),
    ])
      .then(([version, channels]) => {
        const {
          nodes: hyNodes,
          edges: hyEdges,
          rootId,
          viewport,
          kept,
        } = fromSchemaTree(version.tree as any, channels.keys, channels.ids)
        fitPending.current = true
        following.current = true
        setNodes(hyNodes as any)
        setEdges(hyEdges as any)
        flowState.setRootId(rootId)
        flowState.setTreeCalendar(kept.calendar)
        flowState.setLoaded({
          kept,
          baseline: baselineOf(
            hyNodes,
            hyEdges,
            rootId,
            viewport,
            kept,
            kept.calendar,
            channels.keys
          ),
        })
      })
      .catch(err => {
        if (err instanceof UnsupportedTreeError) {
          setRefusal(err.message)
          return
        }
        console.warn(
          '[NotificationFlow] No active tree (or load error); starting blank:',
          err
        )
        setNodes([blankRoot] as any)
        setEdges([])
        flowState.setRootId(blankRoot.id)
      })
      .finally(() => setHydrating(false))
  }, [siteId, notificationId])

  // A node has one parent, and a condition has condition children or an
  // outcome, never both. The serializer and the engine reject the rest, so
  // the editor refuses them while the user draws.
  const onConnect = useCallback(
    (params: any) => {
      const why = connectionRefusal(nodes, edges, params, flowState.rootId)
      if (why) {
        setConnectionNotice(CONNECTION_REFUSAL_TEXT[why])
        return
      }
      flowState.setInvalidNodeIds([])
      setEdges((eds: any) => addEdge(params, eds))
    },
    [nodes, edges, setEdges, flowState]
  )

  // Ids of added nodes that React Flow has not measured yet. Loaded nodes
  // never enter, so placement never moves a saved layout.
  const unplaced = useRef(new Set<string>())

  // A new node becomes the only selection: React Flow draws the selected node
  // above all others, and Delete then removes only the new node.
  const handleAddNode = (type: string) => {
    following.current = false
    const id = `${Date.now()}`
    unplaced.current.add(id)
    flowState.setInvalidNodeIds([])
    setNodes(prev => [
      ...prev.map(n => ({ ...n, selected: false })),
      {
        id,
        type,
        position: placeInPane(
          visiblePane(flowStore.getState(), headerRef.current!),
          { width: 0, height: 0 },
          []
        ).position,
        data: { label: `New ${type} node` },
        selected: true,
      },
    ])
    setEdges(prev => prev.map(e => ({ ...e, selected: false })))
  }

  // A node's size depends on its type and content, and React Flow keeps the
  // node hidden until it has measured it. The first measurement is therefore
  // the moment to place a new node, before the operator sees it. The other
  // nodes were measured earlier, so `nodes` holds their boxes.
  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      onNodesChange(changes)
      if (changes.some(c => c.type === 'dimensions')) {
        setSizeChanges(n => n + 1)
      }
      for (const change of changes) {
        if (change.type !== 'dimensions' || !change.dimensions) continue
        const { id, dimensions } = change
        if (!unplaced.current.has(id)) continue
        unplaced.current.delete(id)
        const { position, viewport } = placeInPane(
          visiblePane(flowStore.getState(), headerRef.current!),
          dimensions,
          nodes.filter(n => n.id !== id)
        )
        setNodes(prev => prev.map(n => (n.id === id ? { ...n, position } : n)))
        if (viewport) setViewport(viewport)
      }
    },
    [onNodesChange, setNodes, setViewport, flowStore, nodes]
  )

  // Apply the Layer-A error highlight (set by Header on a failed save) as a
  // className on the affected nodes.
  const invalidSet = useMemo(
    () => new Set(flowState.invalidNodeIds),
    [flowState.invalidNodeIds]
  )
  // The tree should start with an affiliation node. A stored tree that does
  // not still opens and saves as it is, because the Gateway schema accepts any
  // condition node as the root.
  const rootIsNotAffiliation = (nodes as any[]).some(
    n => n.id === flowState.rootId && n.type !== 'connection'
  )
  const displayNodes = useMemo(
    () =>
      (nodes as any[]).map(n => ({
        ...n,
        className: invalidSet.has(n.id) ? 'node-error' : '',
      })),
    [nodes, invalidSet]
  )

  if (refusal) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
        }}
      >
        <Typography role="alert" sx={{ maxWidth: 640, textAlign: 'center' }}>
          {refusal}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex' }}>
      <Slide direction="right" in={openSidebar} mountOnEnter unmountOnExit>
        <Box>
          <Sidebar
            items={items}
            handleAddNode={handleAddNode}
            openSidebar={openSidebar}
            setOpenSidebar={setOpenSidebar}
          />
        </Box>
      </Slide>

      <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
        <Header
          ref={headerRef}
          openSidebar={openSidebar}
          setOpenSidebar={setOpenSidebar}
        />
        <style>
          {`
            .react-flow__controls {
              background-color: ${isDarkMode ? '#162645' : '#fff'} !important;
              border: ${isDarkMode ? '1px solid #445876' : '1px solid #eee'} !important;
              border-radius: 8px !important;
              padding: 4px !important;
              margin: 4px !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              gap: 8px !important;
            }
            .react-flow__controls-button {
              background-color: transparent !important;
              border-bottom: transparent !important;
              color: ${isDarkMode ? '#FFFFFF' : 'black'} !important;
            }
            .react-flow__controls-button:last-child {
              border-bottom: none !important;
            }
            .react-flow__controls-button:hover {
              background-color: ${isDarkMode ? '#202B40' : '#f0f0f0'} !important;
            }
            .react-flow__controls-button svg {
              fill: ${isDarkMode ? '#BBD7FF' : '#000'} !important;
            }
            .react-flow__node.node-error {
              outline: 2px solid #E5484D;
              outline-offset: 2px;
              border-radius: 8px;
            }
          `}
        </style>
        <ReactFlow
          nodes={displayNodes}
          edges={edges}
          onNodesChange={handleNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onMove={(_, vp: Viewport) => flowState.setViewport(vp)}
          onMoveStart={event => {
            if (event) following.current = false
          }}
          onNodeDragStart={() => {
            following.current = false
          }}
          minZoom={EDITOR_MIN_ZOOM}
          attributionPosition="top-right"
          nodeTypes={nodeTypes}
          // Clicks that wobble <=4px stay clicks, not micro-drags; double-clicks
          // inside form controls must not zoom the canvas.
          nodeDragThreshold={4}
          zoomOnDoubleClick={false}
          // Delete only: xyflow's input guard skips MODIFIED keys, so with
          // Backspace enabled Ctrl+Backspace (delete-word) inside a node's
          // text field would delete the node itself.
          deleteKeyCode={['Delete']}
          onBeforeDelete={async ({ nodes: toDelete, edges: edgesToDelete }) => {
            // Root is not deletable — filter it out of any deletion payload
            // (its incident edges may still be deleted when a CHILD is removed).
            const kept = toDelete.filter(n => n.id !== flowState.rootId)
            if (kept.length === toDelete.length) return true
            return { nodes: kept, edges: edgesToDelete }
          }}
        >
          <Controls />
          <Background
            variant={BackgroundVariant.Dots}
            color={'#374151'}
            gap={12}
            size={1}
            style={{ backgroundColor: isDarkMode ? '#192336' : '#fff' }}
          />
        </ReactFlow>
        {rootIsNotAffiliation && (
          <Alert
            // role status: this is a standing hint, not an error to announce.
            role="status"
            severity="info"
            variant="filled"
            dir="rtl"
            sx={{
              position: 'absolute',
              top: 120,
              left: 16,
              maxWidth: 320,
              zIndex: 5,
              fontFamily: 'Rubik, sans-serif',
            }}
          >
            {ROOT_NOT_AFFILIATION_NOTICE}
          </Alert>
        )}
        {hydrating && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isDarkMode
                ? 'rgba(25, 35, 54, 0.7)'
                : 'rgba(255, 255, 255, 0.7)',
              zIndex: 10000,
              gap: 2,
              flexDirection: 'column',
            }}
          >
            <CircularProgress size={40} sx={{ color: '#72A4EB' }} />
            <Typography
              sx={{
                color: isDarkMode ? '#fff' : '#003F61',
                fontFamily: 'Rubik, sans-serif',
              }}
            >
              טוען דיאגרמה...
            </Typography>
          </Box>
        )}
      </Box>
      <Snackbar
        open={!!connectionNotice}
        autoHideDuration={6000}
        onClose={() => setConnectionNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          onClose={() => setConnectionNotice(null)}
          variant="filled"
          sx={{ fontFamily: 'Rubik, sans-serif' }}
          dir="rtl"
        >
          {connectionNotice}
        </Alert>
      </Snackbar>
    </Box>
  )
}

// What the editor would save for the tree it just opened. A tree the editor cannot serialize, such as
// one that holds a CRM box, has none, and Save then always publishes.
function baselineOf(...args: Parameters<typeof toSchemaTree>) {
  try {
    return toSchemaTree(...args)
  } catch {
    return null
  }
}

const NotificationFlow = () => {
  const { siteId } = useParams()
  return (
    <ReferenceProvider accountId={siteId}>
      <FlowStateProvider
        initialNodes={[blankRoot] as any}
        initialEdges={[]}
        initialRootId={blankRoot.id}
      >
        <ReactFlowProvider>
          <NotificationFlowInner />
        </ReactFlowProvider>
      </FlowStateProvider>
    </ReferenceProvider>
  )
}

export default NotificationFlow
